/**
 * Task rows: the shape both platforms draw, and the mappings from real tool
 * results into it.
 *
 * A task row is one unit of work with a status the user can read at a glance
 * (spinner with a step number, green check, red cross), an amount beside the
 * label, an optional status pill and a drop-down of detail lines. Two tool
 * families already produce exactly that shape: job applications the agent is
 * tracking, and Nova automation runs.
 */
import type { TFunction } from 'i18next';

export type TaskRowStatus = 'done' | 'running' | 'failed' | 'pending';

export type TaskDetail = { label: string; meta: string };

export type TaskRowItem = {
  key: string;
  label: string;
  /** Right-aligned figure or short qualifier: "7 SKUs", company name. */
  amount?: string;
  status: TaskRowStatus;
  /** Shown inside the ring for running / pending rows. */
  step?: number;
  /** Pill copy. Falls back to the generic completed/failed labels. */
  statusLabel?: string;
  details: TaskDetail[];
};

export type TaskRowsLabels = {
  completed: string;
  failed: string;
  running: string;
  pending: string;
};

export function defaultTaskRowsLabels(t: TFunction): TaskRowsLabels {
  return {
    completed: t('chat.tasks.completed'),
    failed: t('chat.tasks.failed'),
    running: t('chat.tasks.running'),
    pending: t('chat.tasks.pending'),
  };
}

function formatShortDate(value: unknown): string {
  if (!value) return '';
  const date = new Date(value as string);
  if (Number.isNaN(date.getTime())) return '';
  try {
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

function humanize(value: string): string {
  return value.replace(/[_-]+/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

// ---------------------------------------------------------------------------
// Applications (applicationTrack / catalogApplyTrack / applicationListByJob)
// ---------------------------------------------------------------------------

export type ApplicationLike = {
  id?: string;
  job?: { title?: string };
  company?: { name?: string };
  status?: string;
  statusHistory?: { status?: string; at?: string; note?: string }[];
  createdAt?: string;
};

const APPLICATION_DONE = new Set(['applied', 'submitted', 'reviewed', 'shortlisted', 'interview', 'offered', 'hired', 'accepted']);
const APPLICATION_FAILED = new Set(['rejected', 'withdrawn', 'failed', 'declined', 'expired']);
const APPLICATION_RUNNING = new Set(['submitting', 'in_progress', 'processing', 'sending']);

export function applicationStatusToTask(status: string | undefined): TaskRowStatus {
  const key = (status ?? '').toLowerCase();
  if (APPLICATION_DONE.has(key)) return 'done';
  if (APPLICATION_FAILED.has(key)) return 'failed';
  if (APPLICATION_RUNNING.has(key)) return 'running';
  return 'pending';
}

export function applicationsToTaskRows(
  applications: readonly ApplicationLike[],
  t: TFunction,
): TaskRowItem[] {
  return applications.map((application, index) => {
    const latest = application.statusHistory?.[0];
    const status = latest?.status ?? application.status;
    const taskStatus = applicationStatusToTask(status);
    const history = (application.statusHistory ?? []).slice(0, 4);

    const details: TaskDetail[] = history.map((entry) => ({
      label: entry.status ? humanize(entry.status) : t('chat.tasks.statusUpdate'),
      meta: formatShortDate(entry.at),
    }));
    if (details.length === 0 && application.createdAt) {
      details.push({ label: t('chat.tasks.submitted'), meta: formatShortDate(application.createdAt) });
    }

    return {
      key: application.id ?? `application-${index}`,
      label: application.job?.title ?? t('chat.tasks.untitledJob'),
      amount: application.company?.name,
      status: taskStatus,
      step: index + 1,
      statusLabel: status ? humanize(status) : undefined,
      details,
    };
  });
}

// ---------------------------------------------------------------------------
// Nova runs (novaRunHistory)
// ---------------------------------------------------------------------------

export type NovaRunLike = {
  id?: string;
  status?: string;
  triggerType?: string;
  startTime?: string | Date | null;
  endTime?: string | Date | null;
  errorSummary?: string | null;
  metrics?: Record<string, unknown>;
  createdAt?: string | Date;
};

export function novaRunStatusToTask(status: string | undefined): TaskRowStatus {
  switch ((status ?? '').toLowerCase()) {
    case 'succeeded':
    case 'completed':
      return 'done';
    case 'failed':
    case 'killed':
    case 'cancelled':
    case 'canceled':
      return 'failed';
    case 'running':
      return 'running';
    default:
      return 'pending';
  }
}

export function novaRunsToTaskRows(runs: readonly NovaRunLike[], t: TFunction): TaskRowItem[] {
  return runs.map((run, index) => {
    const status = run.status ?? '';
    const details: TaskDetail[] = [];
    if (run.startTime) {
      details.push({ label: t('chat.tasks.started'), meta: formatShortDate(run.startTime) });
    }
    if (run.endTime) {
      details.push({ label: t('chat.tasks.finished'), meta: formatShortDate(run.endTime) });
    }
    if (run.errorSummary) {
      details.push({ label: run.errorSummary, meta: '' });
    }
    const metricEntries = Object.entries(run.metrics ?? {}).slice(0, 3);
    for (const [key, value] of metricEntries) {
      if (typeof value === 'number' || typeof value === 'string') {
        details.push({ label: humanize(key), meta: String(value) });
      }
    }

    return {
      key: run.id ?? `run-${index}`,
      label: t(`nova.runs.runStatus.${status}`, { defaultValue: humanize(status || 'run') }),
      amount: run.triggerType ? humanize(run.triggerType) : undefined,
      status: novaRunStatusToTask(status),
      step: index + 1,
      details,
    };
  });
}
